import { observable, runInAction } from "mobx"

import { Model } from "@/lib/Model.ts"

import type { DMDecoratedText } from "@/lib/client/generated/TypeDefinitions.ts"


export class SlidingTextModel extends Model {
  readonly displayedValue = observable.box<DMDecoratedText | string>("")
  private lastChangeTime = Date.now()
  private timeoutId: ReturnType<typeof setTimeout> | null = null

  constructor(
    initialValue: DMDecoratedText | string,
    private readonly minDisplayDuration: number = 1000
  ) {
    super("SlidingTextModel")
    runInAction(() => this.displayedValue.set(initialValue))
  }

  readonly updateValue = (newValue: DMDecoratedText | string) => {
    const newText = typeof newValue === "string" ? newValue : newValue.value
    const currentText = typeof this.displayedValue.get() === "string"
      ? this.displayedValue.get() as string
      : (this.displayedValue.get() as DMDecoratedText).value

    if (newText === currentText) return

    const now = Date.now()
    const timeSinceLastChange = now - this.lastChangeTime

    if (timeSinceLastChange >= this.minDisplayDuration) {
      this.lastChangeTime = now
      runInAction(() => {
        this.displayedValue.set(newValue)
      })
    } else {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId)
      }
      this.timeoutId = setTimeout(() => {
        this.lastChangeTime = Date.now()
        runInAction(() => {
          this.displayedValue.set(newValue)
        })
      }, this.minDisplayDuration - timeSinceLastChange)
    }
  }

  get text(): string {
    const value = this.displayedValue.get()
    return typeof value === "string" ? value : value.value
  }

  terminate() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
    super.terminate()
  }
}